import CookieService from "./cookie.service";
import { getCountry } from "./localeSupport.service";
import { fetchReq } from "../api/fetch";
import api from "../api";
import config from "../constants/config";
import { logAuditData } from "../utils/auditLogger";
import {
  configureDigitalDataOnSignIn,
  configureDigitalDataOnError,
} from "../utils/adobeAnalytics.utils";
import { CLOUD_PROXY, MFA_PROXY } from "../constants/referrerTypes";

/**
 * Common Signin Service
 * Provides shared functionality for signin components
 */
class CommonSigninService {
  /**
   * Gets site identifier based on hostname
   * @returns {string} - 'China' or 'US'
   */
  static getSite() {
    return window.location.host.includes("thermofisher.cn") ? "China" : "US";
  }

  /**
   * Validates email format
   * @param {string} emailAddress - Email to validate
   * @returns {boolean} - True if valid email format
   */
  static validateEmail(emailAddress) {
    const emailRegex = new RegExp(
      "^(?!.*[.]{2})([a-zA-Z0-9])+([a-zA-Z0-9_\\.\\-])+\\@(([a-zA-Z0-9\\-])+\\.)+([a-zA-Z0-9]{2,6})$",
    );
    return emailRegex.test(emailAddress);
  }

  /**
   * Checks if username is legacy format (not email)
   * @param {string} username - Username to check
   * @returns {boolean} - True if legacy username format
   */
  static isLegacyUsername(username) {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Standard email regex
    return !regex.test(username); // Returns `true` if it's NOT a valid email (Legacy username)
  }

  /**
   * Sets signin cookies
   * @param {string} username - Username to store in cookie
   */
  static setSigninCookies(username) {
    CookieService.setCookie(
      "cip_identifier",
      username.trim(),
      process.env[`REACT_APP_COOKIE_DOMAIN${config.ENV_SUFIX}`],
      60,
    );
    CookieService.setCookie(
      "cip_tenant_selection",
      "false",
      process.env[`REACT_APP_COOKIE_DOMAIN${config.ENV_SUFIX}`],
      0.25,
    ); // 6 hours
  }

  /**
   * Sets signin session storage
   * @param {string} username - Username to store
   */
  static setSigninSessionStorage(username) {
    const trimmedUsername = username.trim();
    sessionStorage.setItem("signin_username", trimmedUsername);
    sessionStorage.setItem(
      "legUserName",
      this.isLegacyUsername(trimmedUsername),
    );
    localStorage.setItem("hideFedMsg", "true");
  }

  /**
   * Gets username from session storage
   * @returns {string|null} - Stored username or null
   */
  static getStoredUsername() {
    return sessionStorage.getItem("signin_username");
  }

  /**
   * Clears signin session storage
   */
  static clearSigninSessionStorage() {
    sessionStorage.removeItem("signin_username");
    sessionStorage.removeItem("legUserName");
    sessionStorage.removeItem("signin_identifier_redirect");
    localStorage.removeItem("hideFedMsg");
  }

  /**
   * Sets Gigya assertion in session storage
   * @param {string} assertion - Gigya assertion token
   */
  static setGigyaAssertion(assertion) {
    sessionStorage.setItem("gig_asert", assertion);
  }

  /**
   * Sets API key in session storage
   * @param {string} apiKey - API key to store
   */
  static setApiKey(apiKey) {
    sessionStorage.setItem("gig_apikey", apiKey);
  }

  /**
   * Sets registration token in session storage
   * @param {string} token - Registration token
   * @param {string} type - Token type ('prt', 'ert', 'crt')
   */
  static setRegToken(token, type = "ert") {
    sessionStorage.setItem(type, token);
  }

  /**
   * Logs error message with service prefix
   * @param {string} message - Error message to log
   * @param {*} error - Optional error object to log
   */
  static logError(message, error = null) {
    if (error) {
      console.error(`[CommonSigninService] ${message}`, error);
    } else {
      console.error(`[CommonSigninService] ${message}`);
    }
  }

  /**
   * Redirects to appropriate proxy page based on referrer
   * Uses Gigya URL utility if available, otherwise manual construction
   */
  static redirectToProxy() {
    const mfaApiKey = this.getEnvVar("REACT_APP_MFA_CDC_API_KEY");
    const dcrApiKey = this.getEnvVar("REACT_APP_DCR_CDC_API_KEY");
    const flowContext = [
      document.referrer,
      sessionStorage.getItem("referrer_state"),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const isDcrFlow =
      flowContext.includes("dcr") ||
      (dcrApiKey && sessionStorage.getItem("gig_apikey") === dcrApiKey);
    const isMfaFlow =
      flowContext.includes(MFA_PROXY) ||
      (mfaApiKey && sessionStorage.getItem("gig_apikey") === mfaApiKey);
    const isCloudFlow = flowContext.includes(CLOUD_PROXY);

    let proxyPage;
    if (isMfaFlow) {
      proxyPage = "MFAproxy.html";
    } else if (isCloudFlow) {
      proxyPage = "cloud-proxy.html";
    } else if (isDcrFlow) {
      proxyPage = "DCRproxy.html";
    } else {
      proxyPage = "proxy.html";
    }
    const proxyPath = `/account-center/${proxyPage}`;

    const targetUrl = window.gigya?.utils?.URL?.addParamsToURL
      ? window.gigya.utils.URL.addParamsToURL(proxyPath, {
        mode: "afterLogin",
      })
      : `${proxyPath}?mode=afterLogin`;

    window.location.href = targetUrl;
  }

  /**
   * Determines the appropriate login link based on cookies and referrer
   * @param {string} referrerPage - The document referrer
   * @returns {string} - The appropriate login link
   */
  static determineLoginLink(referrerPage, type) {
    const cip_ru = CookieService.getCookie("cip_ru");

    let loginLink =
      cip_ru === "true"
        ? type === "cloud"
          ? "simplified-cloud-username.html"
          : "simplified-username.html"
        : type === "cloud"
          ? "cloud-signin-identifier.html"
          : "signin-identifier.html";

    // Check for special referrer conditions
    if (
      referrerPage.indexOf("/signin-identifier?regType=FULL") !== -1 ||
      referrerPage.indexOf("lt?cmd=ViewCart&ShoppingCartKey") !== -1
    ) {
      loginLink = "signin-identifier?regType=FULL";
    }

    return loginLink;
  }

  /**
   * Clears identifier cookie following legacy pattern
   */
  static clearIdentifierCookie() {
    document.cookie =
      "cip_identifier=; path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; domain=.thermofisher.com;";
  }

  /**
   * Checks for GAC cookie to determine OIDC flag
   * @returns {boolean} - True if GAC cookie exists
   */
  static hasGACCookie() {
    const apiKey = process.env[`REACT_APP_CDC_API_KEY${config.ENV_SUFIX}`];
    const gacCookie = CookieService.getCookie(`gac_${apiKey}`);
    return gacCookie !== "" && gacCookie !== null;
  }

  /**
   * Gets current country for signin logic
   * @returns {string} - Current country code
   */
  static getCurrentCountry() {
    return getCountry();
  }

  /**
   * Checks if current country is China
   * @returns {boolean} - True if China
   */
  static isChinaCountry() {
    return this.getCurrentCountry().toUpperCase() === "CN";
  }

  /**
   * Creates federated payload for API call
   * @param {string} username - Username for federation check
   * @returns {object} - Federated payload object
   */
  static createFederatedPayload(pathName, username) {
    return CommonSigninService.redirectUrl(pathName, username).findfedObject;
    // return !cip_referralURL
    //   ? { signInMode: true, username, newVersion: newVersion }
    //   : {
    //       signInMode: true,
    //       username,
    //       authType: "SAML",
    //       referralURL: cip_referralURL,
    //     };
  }

  static redirectUrl(pathName, username) {
    let redirectURI = null;
    let findfedObject = null;
    let referrerUrl =
      process.env[`REACT_APP_CLOUD_REFERER_URL${config.ENV_SUFIX}`];
    let cip_referralURL = CookieService.getCookie("cip_referralURL");
    if (pathName === "signin-identifier.html") {
      redirectURI = "/account-center/signin.html";
      findfedObject = { username: username };
    } else if (pathName === "mfa-signin-identifier.html") {
      redirectURI = "/account-center/mfa-signin.html";
      findfedObject = { username: username };
    } else if (pathName === "dcr-signin-identifier.html") {
      redirectURI = "/account-center/dcr-signin.html";
      findfedObject = { username: username };
    } else if (pathName === "simplified-username.html") {
      redirectURI = "/account-center/simplified-password.html";
      findfedObject = { username: username };
    }
    if (pathName === "cloud-signin-identifier.html") {
      redirectURI = "/account-center/cloud-signin.html";
      if (!!cip_referralURL) {
        referrerUrl = cip_referralURL;
      }
      findfedObject = {
        username,
        signinMode: true,
        authType: "SAML",
        referralURL: referrerUrl,
      };
    } else if (pathName === "simplified-cloud-username.html") {
      redirectURI = "/account-center/simplified-cloud-password.html";
      if (!!cip_referralURL) {
        referrerUrl = cip_referralURL;
      }
      findfedObject = {
        username,
        signinMode: true,
        authType: "SAML",
        referralURL: referrerUrl,
      };
    }
    return { redirectUrl: redirectURI, findfedObject: findfedObject };
  }

  /**
   * Handles federated user redirection
   * @param {object} response - Response from findFederated API
   * @param {function} navigate - React Router navigation function
   */
  static async handleFederatedRedirection(
    response,
    username,
    rpUrls,
    redirectUrl,
    navigate,
  ) {
    const site = this.getSite();

    if (response?.data?.federatedUser) {
      logAuditData(
        "Federated Login Initiation",
        "Federation Initiated",
        response,
        site,
      );

      const cookieDomain =
        process.env[`REACT_APP_COOKIE_DOMAIN${config.ENV_SUFIX}`];

      document.cookie = `cip_ru=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; domain=${cookieDomain};`;

      window.location.href = response.data.redirectURI;
      return;
    }

    await this.handleLiteRegRedirect(username, rpUrls, redirectUrl, navigate);
  }

  /**
   * Navigates to signin password page
   * @param {object} config - Configuration object with environment domain
   */
  static navigateToPasswordPage(config, redirectUrl) {
    window.location.href = `${config.environment.domain}${redirectUrl}`;
  }

  /**
   * Navigates to signin identifier page
   * @param {object} config - Configuration object with environment domain
   */
  static navigateToIdentifierPage(config) {
    // Check referrer or session storage to determine version
    const referrerPage =
      document.referrer || sessionStorage.getItem("referrer_state") || "";
    let versionSuffix = "";

    if (referrerPage.includes("signin-identifier-v1.html")) {
      versionSuffix = "-v1";
    } else if (referrerPage.includes("signin-identifier-v2.html")) {
      versionSuffix = "-v2";
    } else if (referrerPage.includes("signin-identifier-v3.html")) {
      versionSuffix = "-v3";
    }

    window.location.href = `${config.environment.domain}/account-center/signin-identifier${versionSuffix}.html`;
  }

  /**
   * Navigates to MFA email verification page
   * @param {string} apiKey - API key for MFA verification
   */
  static navigateToMFAVerification(apiKey) {
    window.location.href = `${process.env.REACT_APP_VERIFY_EMAIL_URL}?apiKey=${apiKey}`;
  }

  /**
   * Navigates to MFA email verify page
   */
  static navigateToMFAEmailVerify() {
    window.location.href = process.env.REACT_APP_MFA_EMAIL_VERIFY_URL;
  }

  /**
   * Gets environment variable with suffix
   * @param {string} varName - Variable name without suffix
   * @returns {string} - Environment variable value
   */
  static getEnvVar(varName) {
    return process.env[`${varName}${config.ENV_SUFIX}`];
  }

  /**
   * Validates username input
   * @param {string} username - Username to validate
   * @returns {object} - Validation result with isValid and error message
   */
  static validateUsername(username) {
    if (!username || username.trim() === "") {
      return {
        isValid: false,
        error: "Username is a required field",
      };
    }
    return {
      isValid: true,
      error: null,
    };
  }

  /**
   * Validates password input
   * @param {string} password - Password to validate
   * @returns {object} - Validation result with isValid and error message
   */
  static validatePassword(password) {
    if (!password || password.trim() === "") {
      return {
        isValid: false,
        error: "Please enter your password",
      };
    }
    return {
      isValid: true,
      error: null,
    };
  }

  /**
   * Creates login parameters for Gigya service
   * @param {string} username - Username for login
   * @param {string} password - Password for login
   * @returns {object} - Login parameters object
   */
  static createLoginParams(username, password) {
    return {
      loginID: username,
      password: password,
      loginMode: "standard",
    };
  }

  /**
   * Handles successful login response
   * @param {object} response - Login response from Gigya
   * @param {function} sendNotification - Function to send notification
   */
  static handleLoginSuccess(response, sendNotification = null) {
    this.clearSigninSessionStorage();
    // sessionStorage.removeItem('loginRedirected');
    const site = this.getSite();
    logAuditData("Email Login", "Login Success", response, site);

    const storedValue = sessionStorage.getItem("rp_url");

    // Configure Adobe Analytics on sign-in
    configureDigitalDataOnSignIn(response);
    let cipRu = getCountry().toLowerCase() === "cn" ? "email" : "true";

    // Set success cookie
    CookieService.setCookie(
      "cip_ru",
      cipRu,
      process.env[`REACT_APP_COOKIE_DOMAIN${config.ENV_SUFIX}`],
      4320,
    );

    sessionStorage.removeItem("rp_url");

    // Redirect and send notification
    setTimeout(() => this.redirectToProxy(), 500);
    if (sendNotification) {
      sendNotification(response.UID, storedValue);
    }
  }

  /**
   * Handles login error responses based on error codes
   * @param {object} response - Error response from Gigya
   * @returns {object} - Error handling result with action and redirect
   */
  static handleLoginError(response) {
    const errorCode = response.errorCode;
    const responseData = {
      errorCode: errorCode,
      errorMessage: response?.errorDetails,
      UID: response?.UID,
      user: {
        email: response?.profile?.email || sessionStorage.getItem("signin_username"),
      }
    };
    
    this.clearSigninSessionStorage();
    // Configure Adobe Analytics on error

    const site = this.getSite();
    if (errorCode > 500000) {
      logAuditData("Email Login", "Login Failed", responseData, site);
      return {
        action: "redirect",
        url: process.env.REACT_APP_PROXY_ERROR_URL,
      };
    }

    if (errorCode === 403042) {
      logAuditData("Email Login", "Login Failed", responseData, site);
      configureDigitalDataOnError(
        "Incorrect username or password, please try again.",
      );
      return {
        action: "show_error",
        message: "Incorrect username or password, please try again.",
      };
    }

    if (errorCode === 403100) {
      logAuditData("Email Login", "Update Password Pending", responseData, site);
      this.setRegToken(response.regToken, "prt");
      return {
        action: "redirect",
        url: process.env.REACT_APP_UPDATE_EMAIL_URL,
      };
    }

    if (errorCode === 403101 || errorCode === 403102) {
      logAuditData("Email Login", "TFA Initiated", responseData, site);
      this.setRegToken(response?.regToken, "prt");
      this.setRegToken(response?.regToken, "ert");
      sessionStorage.setItem("user_email", response?.requestParams?.loginID);
      return {
        action: "redirectToTFA",
        url: process.env.REACT_APP_MFA_EMAIL_VERIFY_URL,
        regToken: response?.regToken,
        provider: "gigyaEmail",
        mode: "verify",
      };
    }

    if (errorCode === 206001) {
      const missingFields = response.missingRequiredFields;

      if (missingFields.includes("data.verifiedEmailDate")) {
        this.setRegToken(response.regToken, "ert");
        response.profile.email
          ? logAuditData(
            "Email Login",
            "Email Verification Pending",
            responseData,
            site,
          )
          : logAuditData("Email Login", "Missing Email", responseData, site);
        return {
          action: "redirect",
          url: response.profile.email
            ? process.env.REACT_APP_VERIFY_EMAIL_URL
            : process.env.REACT_APP_MISSING_URL,
        };
      }

      if (
        missingFields.includes("preferences.marketing.consent.isConsentGranted")
      ) {
        this.setRegToken(response.regToken, "crt");
        return {
          action: "redirect",
          url: process.env.REACT_APP_MARKETING_URL,
        };
      }

      if (response?.errorMessage === "Account Pending Registration") {
        this.setRegToken(response?.regToken, "ert");
        if (!response?.isVerified) {
          if (response?.profile?.email) {
            window.location.href = process.env.REACT_APP_VERIFY_EMAIL_URL;
          } else {
            window.location.href = process.env.REACT_APP_MISSING_URL;
          }
        } else {
          return {
            action: "finalize_registration",
            regToken: response.regToken,
          };
        }
      }
      if (response.errorCode === 206006) {
        this.setRegToken(response?.regToken, "ert");
        this.setApiKey(this.getEnvVar("REACT_APP_MFA_CDC_API_KEY"));
        this.navigateToMFAVerification(
          this.getEnvVar("REACT_APP_MFA_CDC_API_KEY"),
        );
      }
      if (response.errorCode === 206002) {
        this.setRegToken(response?.regToken, "ert");
        this.setApiKey(this.getEnvVar("REACT_APP_MFA_CDC_API_KEY"));
        return {
          action: "redirectToTFA",
          url: process.env.REACT_APP_MFA_EMAIL_VERIFY_URL,
          regToken: response?.regToken,
          provider: "gigyaEmail",
          mode: "register",
        };
      }
      // if (errorCode === 200009 && response.errorMessage === "Accounts Linked") {
      //     return {
      //         action: "finalize_registration",
      //         regToken: response.regToken
      //     };
      // }
    }

    return {
      action: "unknown_error",
      errorCode: errorCode,
      message: response.errorMessage || "An unknown error occurred",
    };
  }

  /**
   * Handles redirection after finalize registration
   * @param {object} response - Response from finalizeRegistration
   */
  static handleAuthRedirection(response) {
    if (response.errorCode === 0) {
      const site = this.getSite();
      logAuditData("Email Login", "Finalized Registration", response, site);
      window.location.href = process.env.REACT_APP_FEDERATED_AUTHORIZE;
    } else {
      console.log("Finalize registration failed. Error : ", response.errorCode);
    }
  }
  /**
   * Handles redirection for Lite Registration
   * @param {string} username - Username for Lite Registration
   * @param {function} navigate - React Router navigation function
   */
  static async handleLiteRegRedirect(username, rpUrls, redirectUrI, navigate) {
    let regParameters = null;
    let liteReg = false;
    let liteRegCheck = `${api.config.liteRegCheck(username)}`;
    if (Array.isArray(rpUrls)) {
      const createAccountEntry = rpUrls.find(
        (rp) => rp.serviceName === "createAccount",
      );
      if (createAccountEntry) {
        regParameters = createAccountEntry.url.split("?")[1];
      }
    }
    liteRegCheck += "?" + regParameters;
    const liteRegCheckResp = await fetchReq(liteRegCheck, api.http.get());
    const liteRegData = await liteRegCheckResp.json();
    if (liteRegData?.liteUser) {
      liteReg = true;
      redirectUrI = liteRegData.liteRegUrl;
    }
    if (liteReg) {
      window.location.href = redirectUrI;
    } else if (typeof navigate === "function") {
      sessionStorage.setItem("signin_identifier_redirect", "true");
      navigate(redirectUrI, { state: { fromIdentifier: true } });
    } else {
      window.location.href = redirectUrI;
    }
  }
}

export default CommonSigninService;
